/**
 * ViewModel for the Preferences page.
 *
 * Composed from focused sections (subject selection, stem selection, the preferences form, the
 * surgical-guide section and the hip-spine section). Delegates to the preferences store (Model)
 * and the organisation profile store for data.
 *
 * The page edits ONE surgeon, who is not always the signed-in user: a sales rep, assistant,
 * organisation admin or Formus admin sets a surgeon up from their own account. Every card
 * therefore follows the selected subject, by one mechanism: each section contributes a loader to
 * `useSubjectSelection`, which calls them all with the surgeon it captured.
 */
import { computed, toRef } from 'vue';
import type { AxiosRequestConfig } from 'axios';
import { usePreferences } from '@/views/preferences/preferences';
import { useOrganisationProfile } from '@/stores/organisationProfile/store';
import { useHipSpineFeatureGate } from '@/stores/featureToggles/useHipSpineFeatureGate';
import { useSurgicalGuidePreferences } from '@/stores/surgicalGuide/preferencesStore';
import { usePreferencesSubject } from '@/stores/preferencesSubject/preferencesSubjectStore';
import { useHipSpinePreferencesViewModel } from '@/views/preferences/hipSpine/useHipSpinePreferencesViewModel';
import { SURGICAL_APPROACHES } from '@/domain/case/surgery';
import { availableStemSystems } from '@/domain/catalog/stemSystems';
import { useStemSystemGrouping } from '@/domain/catalog/useStemSystemGrouping';
import { alignmentModes } from '@/domain/anatomy/alignmentModes';

/** What one card does when the page settles on a surgeon. */
type SubjectLoad = (surgeonId: number, config: AxiosRequestConfig) => Promise<void>;

function useStemSelection() {
    const preferencesForm = usePreferences();

    const availableStems = computed(() =>
        preferencesForm.options.stems.filter((object) => !object.type.includes('Legacy')),
    );
    const stemSystems = computed(() => availableStemSystems());

    const { stemSystemGroups, selectedStemTypesBySystem, updateSelectedStems } =
        useStemSystemGrouping({
            availableStems,
            selectedStemIds: toRef(preferencesForm.state, 'stems'),
            stemSystems,
        });

    return { stemSystems, stemSystemGroups, selectedStemTypesBySystem, updateSelectedStems };
}

function usePreferencesForm() {
    const preferencesForm = usePreferences();
    const subject = usePreferencesSubject();

    const hasError = computed(
        () => preferencesForm.validationFailed || preferencesForm.saveError !== null,
    );
    const message = computed(() => {
        if (preferencesForm.saveError) return preferencesForm.saveError;
        if (preferencesForm.saveSuccess) {
            return 'Surgical Preferences were successfully updated!';
        }
        return '';
    });

    async function save() {
        if (subject.selectedId === null) return;

        await preferencesForm.save(subject.selectedId);
        // Scroll to top after save attempt (UI concern).
        window.scroll({ top: 0, left: 0, behavior: 'smooth' });
    }

    return {
        state: preferencesForm.state,
        hasUnsavedChanges: computed(() => preferencesForm.hasUnsavedChanges),
        isSaving: computed(() => preferencesForm.isSaving),
        isReady: computed(() => preferencesForm.isReady),
        preferencesLoadFailed: computed(() => preferencesForm.loadFailed),
        hasError,
        message,
        checkFieldError: (field: string) => preferencesForm.hasError(field),
        save,
        loadPreferences: async (surgeonId: number, config?: AxiosRequestConfig) => {
            await preferencesForm.load(surgeonId, config);
        },
    };
}

function useSurgicalGuidePreferencesSection() {
    const organisationProfile = useOrganisationProfile();
    const surgicalGuideStore = useSurgicalGuidePreferences();
    const subject = usePreferencesSubject();

    const showSurgicalGuide = computed(() => organisationProfile.features.order_surgical_guide);

    return {
        showSurgicalGuide,
        surgicalApproachId: computed(() => surgicalGuideStore.current.surgicalApproachId),
        surgicalApproachOptions: computed(() => SURGICAL_APPROACHES),
        surgicalGuideHasUnsavedChanges: computed(() => surgicalGuideStore.hasUnsavedChanges),
        saveSurgicalGuide: async () => {
            if (!showSurgicalGuide.value || subject.selectedId === null) return;
            await surgicalGuideStore.save(subject.selectedId);
        },
        loadSurgicalGuide: async (surgeonId: number, config?: AxiosRequestConfig) => {
            if (!showSurgicalGuide.value) return;
            await surgicalGuideStore.load(surgeonId, config);
        },
        updateSurgicalApproach: (value: string) => {
            surgicalGuideStore.current.surgicalApproachId = value;
        },
    };
}

function useHipSpineSection() {
    const { enabled: showHipSpine } = useHipSpineFeatureGate();
    const hipSpine = useHipSpinePreferencesViewModel();

    return {
        showHipSpine,
        loadHipSpine: async (surgeonId: number, config?: AxiosRequestConfig) => {
            if (!showHipSpine.value) return;
            await hipSpine.load(surgeonId, config);
        },
    };
}

/**
 * Subject selection, and the fan-out that puts every card on the same surgeon.
 *
 * The surgeon is captured once per selection and passed to each loader, so two cards can never
 * settle on different subjects. The previous selection's requests are aborted, so a slow response
 * for the surgeon the caller has moved off cannot land on top of the one they are now editing.
 *
 * Each loader records a failed request in its own card's state rather than rejecting, so the
 * bootstrap can start this and leave it running.
 */
function useSubjectSelection(loads: SubjectLoad[]) {
    const subject = usePreferencesSubject();
    let inFlight: AbortController | null = null;

    async function loadSelectedSurgeon(): Promise<void> {
        inFlight?.abort();
        inFlight = null;

        const surgeonId = subject.selectedId;
        if (surgeonId === null) return;

        const selection = new AbortController();
        inFlight = selection;
        await Promise.all(loads.map((load) => load(surgeonId, { signal: selection.signal })));
    }

    return {
        surgeonOptions: computed(() => subject.options),
        selectedSurgeonId: computed(() => subject.selectedId),
        isSubjectSelected: computed(() => subject.selectedId !== null),
        hasSurgeonOptions: computed(() => subject.hasOptions),
        isSubjectReady: computed(() => subject.isReady),
        subjectLoadFailed: computed(() => subject.loadFailed),
        isEditingOwnPreferences: computed(() => subject.isEditingSelf),
        /**
         * Bootstrap. Only the surgeon list is awaited, because the page cannot draw its subject
         * card without it; the cards then load behind the rendered page rather than behind the
         * Suspense fallback. Each one already hides itself until its own data arrives, which is
         * what a surgeon switch has always looked like.
         */
        loadSurgeons: async () => {
            await subject.load();
            void loadSelectedSurgeon();
        },
        selectSurgeon: async (id: number) => {
            subject.select(id);
            await loadSelectedSurgeon();
        },
    };
}

export function usePreferencesViewModel() {
    const form = usePreferencesForm();
    const surgicalGuide = useSurgicalGuidePreferencesSection();
    const hipSpine = useHipSpineSection();

    return {
        alignmentModeOptions: alignmentModes(),
        ...useStemSelection(),
        ...form,
        ...surgicalGuide,
        ...hipSpine,
        ...useSubjectSelection([
            form.loadPreferences,
            surgicalGuide.loadSurgicalGuide,
            hipSpine.loadHipSpine,
        ]),
    };
}
